import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./owner-info.css";

// ErrorBoundary component to catch rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div className="error-message">Something went wrong.</div>;
    }
    return this.props.children;
  }
}

/**
 * Truncate an HTML string based on its character count.
 * @param {string} html - The HTML string to truncate.
 * @param {number} maxLength - The maximum allowed character count.
 * @returns {string} - The truncated HTML.
 */
function truncateHTML(html, maxLength) {
  try {
    if (typeof html !== "string") {
      html = String(html);
    }
    if (html.length <= maxLength) return html;
    return html.slice(0, maxLength) + "...";
  } catch (error) {
    console.error("Error in truncateHTML:", error);
    return html;
  }
}

/**
 * Minify HTML by collapsing whitespace and normalizing <br> tags.
 * @param {string} html - The HTML string to minify.
 * @returns {string} - The minified HTML.
 */
function minifyHTML(html) {
  try {
    if (typeof html !== "string") {
      html = String(html);
    }
    return html
      .replace(/\s+/g, " ") // collapse multiple spaces
      .replace(/\s*<br\s*\/?>\s*/gi, "<br>") // normalize <br> tags
      .trim();
  } catch (error) {
    console.error("Error in minifyHTML:", error);
    return html;
  }
}

/**
 * Remove all HTML tags from a string.
 * @param {string} html - The HTML string.
 * @returns {string} - The plain text.
 */
function stripHtml(html) {
  try {
    return html.replace(/<[^>]*>/g, "");
  } catch (error) {
    console.error("Error in stripHtml:", error);
    return html;
  }
}

function OwnerWelcome({ sentence }) {
  const { t } = useTranslation(["publicEvents"]);
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 300;

  // Ensure sentence is a string and minify it
  const safeSentence = typeof sentence === "string" ? sentence : "";
  const cleanSentence = minifyHTML(safeSentence);

  // Get plain text for length-check
  const plainText = stripHtml(cleanSentence);

  const sentenceShort =
    plainText.length > maxLength
      ? truncateHTML(cleanSentence, maxLength)
      : cleanSentence;

  const toggleText = () => {
    try {
      setIsExpanded((prev) => !prev);
    } catch (error) {
      console.error("Error in toggleText:", error);
    }
  };

  return (
    <div className="bio-container">
      <span
        className={isExpanded ? "expanded" : "collapsed"}
        dangerouslySetInnerHTML={{
          __html: isExpanded ? cleanSentence : sentenceShort,
        }}
      />
      {plainText.length > maxLength && (
        <b onClick={toggleText} className="toggle-text">
          {isExpanded ? t("showLess") : t("showMore")}
        </b>
      )}
    </div>
  );
}

// Wrap the component with an error boundary for enhanced resilience.
function OwnerWelcomeWithErrorBoundary(props) {
  return (
    <ErrorBoundary>
      <OwnerWelcome {...props} />
    </ErrorBoundary>
  );
}

export default OwnerWelcomeWithErrorBoundary;
